/**
 * Product Link Patterns for Blog Internal Linking
 * Maps keyword patterns → Shopify product handles.
 * Used by RichArticleText to inject contextual links into blog content at render time.
 *
 * Anti-duplication rules (enforced in RichArticleText):
 * - Each handle is linked at most ONCE per article
 * - Handles already shown in ProductCTA are excluded
 */

export interface LinkPattern {
  pattern: RegExp;
  handle: string;
  /** Max times this handle may be linked in a single section (default: 1) */
  maxPerSection: number;
}

export const PRODUCT_LINK_PATTERNS: LinkPattern[] = [
  // ── Pink Glow Serum ──────────────────────────────────────────────────
  // English
  { pattern: /\bPDRN serum\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  { pattern: /\bPink Glow Serum\b/gi,      handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // French
  { pattern: /\bsérum PDRN\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  { pattern: /\bSérum PDRN\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // Spanish
  { pattern: /\bSuero PDRN\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // Italian
  { pattern: /\bsiero PDRN\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // German
  { pattern: /\bPDRN-Serum\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // Norwegian
  { pattern: /\bPDRN-serum\b/gi,           handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // Finnish
  { pattern: /\bPDRN-seerumi\b/gi,         handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // Hebrew (no \b — doesn't work with non-Latin Unicode)
  { pattern: /סרום PDRN/g,                 handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  { pattern: /סרום Pink Glow/g,            handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  // Arabic (no \b)
  { pattern: /سيروم PDRN/g,               handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },
  { pattern: /سيروم بيبتيد/g,             handle: 'pink-glow-serum-with-pdrn-peptides', maxPerSection: 1 },

  // ── PDRN Pink Sheet Masks ─────────────────────────────────────────────
  // English
  { pattern: /\bPDRN sheet mask/gi,        handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /\bPDRN Sheet Mask/gi,        handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // French — singular + plural
  { pattern: /\bmasques PDRN\b/gi,         handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /\bmasque PDRN\b/gi,          handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // Spanish — singular + plural
  { pattern: /\bmascarillas PDRN\b/gi,     handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /\bmascarilla PDRN\b/gi,      handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // German — with hyphen (PDRN-Tuchmaske/n) and without (PDRN Tuchmaske)
  { pattern: /\bPDRN Tuchmaske/gi,         handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /\bPDRN-Tuchmaske/gi,         handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // Norwegian
  { pattern: /\bPDRN-sheetmasker\b/gi,     handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /\bPDRN-sheetmaske\b/gi,      handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // Finnish
  { pattern: /\bPDRN-arkkimaskit\b/gi,     handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // Croatian
  { pattern: /\bsheet maske PDRN\b/gi,     handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /\bPDRN sheet maske\b/gi,     handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // Hebrew
  { pattern: /מסכות PDRN/g,               handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  { pattern: /מסכות הבד/g,                handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },
  // Arabic
  { pattern: /أقنعة PDRN/g,               handle: 'pdrn-pink-sheet-masks-10-pack', maxPerSection: 1 },

  // ── Smart Capsule Cream ───────────────────────────────────────────────
  // English
  { pattern: /\bSmart Capsule Cream\b/gi,  handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /\bcapsule cream\b/gi,        handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // French
  { pattern: /\bcrème Smart Capsule\b/gi,  handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // German
  { pattern: /\bKapselcreme\b/gi,          handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /\bPDRN-Kapselcreme\b/gi,     handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // Spanish
  { pattern: /\bcrema de cápsulas\b/gi,    handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // Norwegian
  { pattern: /\bPDRN-kapselkrem\b/gi,      handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /\bkapselkrem\b/gi,           handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // Finnish
  { pattern: /\bPDRN-kapselivoide\b/gi,    handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /\bkapselivoide\b/gi,         handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // Croatian
  { pattern: /\bkapselna krema\b/gi,       handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /\bkapsulna krema\b/gi,       handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // Hebrew
  { pattern: /קרם הקפסולות/g,             handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /קרם קפסולות חכם/g,          handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  // Arabic
  { pattern: /كريم الكبسولات الذكي/g,    handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },
  { pattern: /كريم الكبسولات/g,          handle: 'smart-capsule-cream-with-collagen', maxPerSection: 1 },

  // ── Complete Trio Bundle ──────────────────────────────────────────────
  // English
  { pattern: /\bComplete Trio\b/gi,              handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /\btrio PDRN\b/gi,                  handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /\bfull PDRN skincare system\b/gi,  handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // German
  { pattern: /\bPDRN-Trio\b/gi,                  handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // French
  { pattern: /\btrio complet PDRN\b/gi,          handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /\bnotre trio PDRN\b/gi,            handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // Spanish — trío with accent (regex /i does NOT normalize accents, needs separate pattern)
  { pattern: /\btrío PDRN\b/gi,                  handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /\btrío completo\b/gi,              handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /\btrio completo\b/gi,              handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // Norwegian + Finnish (same term)
  { pattern: /\bPDRN-trio\b/gi,                  handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // Croatian
  { pattern: /\bPDRN trio\b/gi,                  handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // Hebrew
  { pattern: /השלישייה המלאה/g,                 handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /הסט המלא/g,                       handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  // Arabic
  { pattern: /ثلاثي PDRN/g,                     handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
  { pattern: /مجموعة PDRN/g,                    handle: 'complete-pdrn-trio-bundle', maxPerSection: 1 },
];
